import React, { useEffect, useRef, useState } from "react";

// ─── Color tokens matching BerBul design system ───────────────────────────────
const C = {
  bg:      "#08101e",
  surface: "#0c1525",
  border:  "#1a2a3a",
  green:   "#00e896",
  cyan:    "#00d4ff",
  red:     "#ff4757",
  text:    "#c8d8e8",
  muted:   "#4a6080",
  white:   "#f0f8ff",
};

// ─── Inline styles ─────────────────────────────────────────────────────────────
const S = {
  page: {
    background: C.bg,
    color: C.text,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    minHeight: "100vh",
    overflowX: "hidden",
  },

  // ── HERO ──────────────────────────────────────────────────────────────────
  hero: {
    position: "relative",
    padding: "100px 0 80px",
    textAlign: "center",
    borderBottom: `1px solid ${C.border}`,
    overflow: "hidden",
  },
  heroGrid: {
    position: "absolute", inset: 0, opacity: 0.04,
    backgroundImage: `linear-gradient(${C.cyan} 1px, transparent 1px), linear-gradient(90deg, ${C.cyan} 1px, transparent 1px)`,
    backgroundSize: "60px 60px",
    pointerEvents: "none",
  },
  heroGlow: {
    position: "absolute", top: "50%", left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600, height: 300,
    background: `radial-gradient(ellipse, ${C.green}18 0%, transparent 70%)`,
    pointerEvents: "none",
  },
  eyebrow: {
    fontSize: 11, letterSpacing: "0.25em", color: C.green,
    textTransform: "uppercase", marginBottom: 20,
    fontWeight: 600,
  },
  heroTitle: {
    fontSize: "clamp(36px, 5vw, 64px)",
    fontWeight: 700, lineHeight: 1.1,
    color: C.white, marginBottom: 24,
    letterSpacing: "-0.02em",
  },
  heroAccent: {
    color: C.green,
  },
  heroSub: {
    fontSize: 18, color: C.muted, maxWidth: 560,
    margin: "0 auto 40px", lineHeight: 1.7,
  },

  // ── STAT ROW ──────────────────────────────────────────────────────────────
  statRow: {
    display: "flex", justifyContent: "center", gap: 48,
    flexWrap: "wrap", position: "relative", zIndex: 1,
  },
  statItem: {
    textAlign: "center",
  },
  statNum: {
    fontSize: 36, fontWeight: 700, color: C.green,
    display: "block", lineHeight: 1,
  },
  statLabel: {
    fontSize: 11, color: C.muted, letterSpacing: "0.15em",
    textTransform: "uppercase", marginTop: 6, display: "block",
  },

  // ── SECTION SHARED ────────────────────────────────────────────────────────
  section: {
    maxWidth: 860, margin: "0 auto", padding: "80px 24px",
  },
  sectionNum: {
    fontSize: 11, color: C.green, letterSpacing: "0.2em",
    textTransform: "uppercase", marginBottom: 16, display: "block",
    fontWeight: 600,
  },
  sectionTitle: {
    fontSize: "clamp(26px, 3vw, 40px)", fontWeight: 700,
    color: C.white, marginBottom: 24, lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  prose: {
    fontSize: 17, lineHeight: 1.85, color: C.text,
    marginBottom: 20,
  },
  proseBold: {
    color: C.white, fontWeight: 600,
  },
  divider: {
    border: "none", borderTop: `1px solid ${C.border}`,
    margin: "0",
  },

  // ── CALLOUT ───────────────────────────────────────────────────────────────
  callout: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderLeft: `3px solid ${C.green}`,
    padding: "24px 28px",
    borderRadius: 8,
    margin: "32px 0",
  },
  calloutText: {
    fontSize: 16, color: C.text, lineHeight: 1.75,
    margin: 0, fontStyle: "italic",
  },

  // ── COMPARISON TABLE ──────────────────────────────────────────────────────
  compTable: {
    width: "100%", borderCollapse: "collapse",
    marginTop: 32,
  },
  compHead: {
    background: C.surface,
    fontSize: 11, letterSpacing: "0.15em",
    textTransform: "uppercase", color: C.muted,
    padding: "12px 16px", textAlign: "left",
    borderBottom: `1px solid ${C.border}`,
    fontWeight: 600,
  },
  compHeadGreen: {
    color: C.green,
  },
  compRow: {
    borderBottom: `1px solid ${C.border}`,
  },
  compCell: {
    padding: "14px 16px", fontSize: 14,
    color: C.muted, verticalAlign: "top",
    lineHeight: 1.5,
  },
  compCellGreen: {
    color: C.green, fontWeight: 600,
  },
  compCellWhite: {
    color: C.white,
  },

  // ── LAYER CARDS ───────────────────────────────────────────────────────────
  layerGrid: {
    display: "grid", gap: 16,
    gridTemplateColumns: "1fr",
    marginTop: 40,
  },
  layerCard: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: "24px 28px",
    display: "flex", gap: 24, alignItems: "flex-start",
  },
  layerNum: {
    fontSize: 11, color: C.green, letterSpacing: "0.15em",
    fontWeight: 700, minWidth: 28, paddingTop: 2,
  },
  layerContent: { flex: 1 },
  layerName: {
    fontSize: 13, fontWeight: 700, color: C.white,
    letterSpacing: "0.1em", textTransform: "uppercase",
    marginBottom: 6,
  },
  layerDesc: {
    fontSize: 14, color: C.text, lineHeight: 1.65,
  },

  // ── TRADE EXAMPLE ─────────────────────────────────────────────────────────
  tradeBox: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10, overflow: "hidden",
    marginTop: 40,
  },
  tradeHeader: {
    background: "#0a1822",
    padding: "14px 20px",
    fontSize: 11, letterSpacing: "0.2em",
    textTransform: "uppercase", color: C.green,
    fontWeight: 700, borderBottom: `1px solid ${C.border}`,
  },
  tradeRow: {
    display: "grid",
    gridTemplateColumns: "100px 120px 1fr",
    padding: "14px 20px",
    borderBottom: `1px solid ${C.border}`,
    fontSize: 13, alignItems: "center", gap: 16,
  },
  tradeTime: { color: C.muted, fontFamily: "inherit" },
  tradeBadge: {
    display: "inline-block", padding: "3px 10px",
    borderRadius: 4, fontSize: 11, fontWeight: 700,
    letterSpacing: "0.08em",
  },
  tradeDetail: { color: C.text, lineHeight: 1.5 },

  // ── EVALUATION LISTS ─────────────────────────────────────────────────────
  evalGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24, marginTop: 40,
  },
  evalCard: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    overflow: "hidden",
  },
  evalHead: {
    padding: "14px 20px",
    fontSize: 11, letterSpacing: "0.2em",
    textTransform: "uppercase", fontWeight: 700,
    borderBottom: `1px solid ${C.border}`,
  },
  evalList: {
    listStyle: "none", padding: "8px 0", margin: 0,
  },
  evalItem: {
    padding: "10px 20px",
    fontSize: 13, lineHeight: 1.5,
    borderBottom: `1px solid ${C.border}`,
    display: "flex", gap: 10, alignItems: "flex-start",
  },
  evalDot: { marginTop: 3, flexShrink: 0 },

  // ── FINAL CTA ─────────────────────────────────────────────────────────────
  ctaSection: {
    textAlign: "center",
    padding: "80px 24px",
    borderTop: `1px solid ${C.border}`,
    background: C.surface,
  },
  ctaTitle: {
    fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 700,
    color: C.white, marginBottom: 16,
  },
  ctaSub: {
    fontSize: 16, color: C.muted, marginBottom: 36,
    maxWidth: 480, margin: "0 auto 36px",
  },
  btnPrimary: {
    display: "inline-block",
    padding: "14px 36px",
    background: C.green, color: "#08101e",
    fontFamily: "'DM Mono', monospace",
    fontWeight: 700, fontSize: 14,
    letterSpacing: "0.08em", textTransform: "uppercase",
    border: "none", borderRadius: 8,
    cursor: "pointer", textDecoration: "none",
    transition: "opacity 0.2s",
  },
};

// ─── Data ──────────────────────────────────────────────────────────────────────
const LAYERS = [
  { n:"01", name:"Predict", desc:"Three AI models trained exclusively on SPY 0DTE data forecast price 10, 20 and 60 minutes ahead. Not generic signals — a proprietary understanding of how SPY moves specifically, built from years of tick data." },
  { n:"02", name:"Validate", desc:"A reinforcement learning model cross-checks every signal against thousands of simulated trades. It has learned to recognise the specific patterns that precede losses — acting as a disciplined risk filter before any capital moves." },
  { n:"03", name:"Decide",  desc:"The agent combines both signals with live market context and makes the final call — PROCEED or SKIP. It sets the entry price, target, stop loss, selects the options strike, and defines the trade timeframe." },
  { n:"04", name:"Monitor", desc:"Every 5 minutes the monitoring agent re-evaluates the open position. HOLD, ADJUST, or CLOSE — based on current conditions, not the original plan. It never holds a losing position hoping the market turns." },
  { n:"05", name:"Learn",   desc:"Short and long-term memory systems capture what worked and what didn't, session by session. The agent gets incrementally smarter with every trade it closes — compounding intelligence alongside capital." },
];

const TRADE_ROWS = [
  { time:"10:06:25", badge:"PROCEED", badgeBg:"#003d28", badgeColor:C.green,  detail:"SPY PUT $691 strike · 10 contracts · entry $2.05. Confidence: 74%. 11 similar historical cases, 73% win rate." },
  { time:"10:06:26", badge:"ENTERED", badgeBg:"#002a3d", badgeColor:C.cyan,   detail:"Capital deployed: $2,050. Stop loss at $683.97. Target at $688.12." },
  { time:"10:07:45", badge:"HOLD",    badgeBg:"#1a2a0a", badgeColor:"#8bc34a",detail:"5-min check. Momentum building. Position up $0.12. Original thesis intact." },
  { time:"10:08:10", badge:"CLOSED",  badgeBg:"#003d28", badgeColor:C.green,  detail:"Target hit. Closed at $2.17. Net return: +$120. Total hold: 1 min 19 sec." },
];

const EVAL_DO = [
  "Win rate holding above 70% over 20+ sessions",
  "Losing days are small and contained — not blow-ups",
  "Capital is back flat in your account at 4pm every day",
  "The reasoning behind each decision is readable and logical",
];

const EVAL_DONT = [
  "\"Why did it skip that setup? I would have taken it.\"",
  "\"Have you tried adding the VWAP indicator?\"",
  "\"I saw a YouTube strategy that might work better.\"",
  "\"What if we changed the stop loss logic?\"",
];

// ─── Component ─────────────────────────────────────────────────────────────────
function OurApproach() {
  // Simple fade-in on scroll
  const [visible, setVisible] = useState({});
  const refs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) setVisible(v => ({ ...v, [e.target.dataset.key]: true }));
        });
      },
      { threshold: 0.1 }
    );
    Object.values(refs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const fadeIn = (key) => ({
    opacity: visible[key] ? 1 : 0,
    transform: visible[key] ? "translateY(0)" : "translateY(24px)",
    transition: "opacity 0.6s ease, transform 0.6s ease",
  });

  const ref = (key) => (el) => {
    refs.current[key] = el;
    if (el) el.dataset.key = key;
  };

  return (
    <div style={S.page}>

      {/* ── HERO ── */}
      <section style={S.hero}>
        <div style={S.heroGrid} />
        <div style={S.heroGlow} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <p style={S.eyebrow}>Our Approach · BerBul AI Trading</p>
          <h1 style={S.heroTitle}>
            The problem isn't<br />
            <span style={S.heroAccent}>your strategy.</span>
          </h1>
          <p style={S.heroSub}>
            You've spent real money learning to trade. The issue was never knowledge.
            It was execution — and execution is a human problem.
          </p>
          <div style={S.statRow}>
            {[
              ["78%",  "Win Rate"],
              ["97s",  "Avg Hold Time"],
              ["$487", "Avg Daily Return"],
              ["18/22","Profitable Days"],
            ].map(([n, l]) => (
              <div key={l} style={S.statItem}>
                <span style={S.statNum}>{n}</span>
                <span style={S.statLabel}>{l}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 16 }}>
            Paper trading · SPY 0DTE · Feb 2026 · All sessions included
          </p>
        </div>
      </section>

      {/* ── SECTION 01: The Real Problem ── */}
      <hr style={S.divider} />
      <section style={S.section}>
        <div ref={ref("s1")} style={fadeIn("s1")}>
          <span style={S.sectionNum}>01 — The Real Problem</span>
          <h2 style={S.sectionTitle}>You didn't fail.<br />The approach failed.</h2>

          <p style={S.prose}>
            Research from UC Berkeley tracked 360,000 retail traders over 15 years and asked a simple question:
            why do most of them lose money? The answer was not bad strategy selection.
            It was <span style={S.proseBold}>execution inconsistency</span> — the same trader making different
            decisions on identical setups depending on mood, recent results, and emotional state.
          </p>
          <p style={S.prose}>
            You found a pattern that worked in specific market conditions. Those conditions changed.
            The pattern stopped working. You blamed yourself, or moved on to the next strategy.
            That cycle will keep repeating — not because the indicators are wrong, but because the
            fundamental problem with discretionary trading is the person doing it.
          </p>

          <div style={S.callout}>
            <p style={S.calloutText}>
              "The edge existed. The human destroyed it."
            </p>
          </div>

          <p style={S.prose}>
            Holding losing positions too long because of hope. Closing winners too early because of fear.
            Overtrading after a loss. Making different decisions on the same setup depending on tiredness.
            These are not character flaws — they are predictable biological responses to financial risk.
            No amount of discipline training eliminates them reliably.
          </p>
          <p style={S.prose}>
            The question is not: how do I get better at trading?
            The question is: <span style={S.proseBold}>how do I remove myself from the equation?</span>
          </p>
        </div>
      </section>

      {/* ── SECTION 02: What BerBul Is (and isn't) ── */}
      <hr style={S.divider} />
      <section style={{ ...S.section, background: C.surface }}>
        <div ref={ref("s2")} style={fadeIn("s2")}>
          <span style={S.sectionNum}>02 — What BerBul Actually Is</span>
          <h2 style={S.sectionTitle}>Not a tool.<br />A fully autonomous agent.</h2>

          <p style={S.prose}>
            BerBul is not a signal service. It is not copy trading where you follow someone else's
            decisions with the same emotional reactions, just delayed. It is not a strategy to learn,
            apply, and eventually master.
          </p>
          <p style={S.prose}>
            BerBul is a <span style={S.proseBold}>fully autonomous trading agent</span>. It makes every decision.
            The only decisions you make are: how much capital to deploy, when to start and stop running the agent,
            and whether to review the session logs afterward. That's it.
          </p>
          <p style={S.prose}>
            Every entry, every monitoring check, every exit — handled by a five-layer AI system that has
            no emotions, no impatience, and no ego.
          </p>

          {/* Layer cards */}
          <div style={S.layerGrid}>
            {LAYERS.map(l => (
              <div key={l.n} style={S.layerCard}>
                <span style={S.layerNum}>{l.n}</span>
                <div style={S.layerContent}>
                  <div style={S.layerName}>{l.name}</div>
                  <div style={S.layerDesc}>{l.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 03: A Real Trade ── */}
      <hr style={S.divider} />
      <section style={S.section}>
        <div ref={ref("s3")} style={fadeIn("s3")}>
          <span style={S.sectionNum}>03 — A Real Trade, Step by Step</span>
          <h2 style={S.sectionTitle}>79 seconds.<br />No human input.</h2>

          <p style={S.prose}>
            This is what a real trade looked like on February 26, 2026.
            The full reasoning behind every decision is logged and readable in your session dashboard.
          </p>

          <div style={S.tradeBox}>
            <div style={S.tradeHeader}>Live Session · Feb 26 2026 · SPY 0DTE</div>
            {TRADE_ROWS.map((r, i) => (
              <div key={i} style={{ ...S.tradeRow, borderBottom: i < TRADE_ROWS.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <span style={S.tradeTime}>{r.time}</span>
                <span style={{ ...S.tradeBadge, background: r.badgeBg, color: r.badgeColor }}>
                  {r.badge}
                </span>
                <span style={S.tradeDetail}>{r.detail}</span>
              </div>
            ))}
          </div>

          <p style={{ ...S.prose, marginTop: 24, color: C.muted, fontSize: 14 }}>
            Most setups are skipped. A disciplined agent that takes 10 high-quality trades is worth
            far more than one that trades 40 marginal ones. On Feb 25th, the agent evaluated four
            setups and skipped three. That restraint is part of the edge.
          </p>
        </div>
      </section>

      {/* ── SECTION 04: The Mental Model Shift ── */}
      <hr style={S.divider} />
      <section style={{ ...S.section, background: C.surface }}>
        <div ref={ref("s4")} style={fadeIn("s4")}>
          <span style={S.sectionNum}>04 — The Mental Model Shift</span>
          <h2 style={S.sectionTitle}>You are the owner.<br />Not the operator.</h2>

          <p style={S.prose}>
            Think about how you relate to other professionals you hire. When you hire an accountant,
            you don't sit next to them suggesting which deductions to take. When you hire a surgeon,
            you don't advise on which incision to make. You hire them for their expertise and
            evaluate the outcome.
          </p>
          <p style={S.prose}>
            BerBul is the same relationship. You decide how much capital to allocate.
            The agent executes the strategy. You evaluate the results over time.
          </p>

          <div style={S.callout}>
            <p style={S.calloutText}>
              The moment you start second-guessing individual trades — "why did it skip that setup?"
              or "I would have taken that trade" — you are reintroducing the exact problem the system
              was designed to solve.
            </p>
          </div>

          <p style={S.prose}>
            The correct evaluation window is a minimum of 20 sessions, ideally 40.
            A single session tells you almost nothing. Here is what to measure — and what to ignore.
          </p>

          {/* Evaluate / Don't evaluate */}
          <div style={S.evalGrid}>
            <div style={S.evalCard}>
              <div style={{ ...S.evalHead, background: "#003d28", color: C.green }}>
                Evaluate the system on these
              </div>
              <ul style={S.evalList}>
                {EVAL_DO.map((item, i) => (
                  <li key={i} style={{ ...S.evalItem, borderBottom: i < EVAL_DO.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <span style={{ ...S.evalDot, color: C.green }}>+</span>
                    <span style={{ color: C.text, fontSize: 13, lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={S.evalCard}>
              <div style={{ ...S.evalHead, background: "#3d0a0a", color: C.red }}>
                Do not evaluate it on these
              </div>
              <ul style={S.evalList}>
                {EVAL_DONT.map((item, i) => (
                  <li key={i} style={{ ...S.evalItem, borderBottom: i < EVAL_DONT.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <span style={{ ...S.evalDot, color: C.red }}>—</span>
                    <span style={{ color: C.muted, fontSize: 13, lineHeight: 1.5, fontStyle: "italic" }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 05: Transparency ── */}
      <hr style={S.divider} />
      <section style={S.section}>
        <div ref={ref("s5")} style={fadeIn("s5")}>
          <span style={S.sectionNum}>05 — A Glass Box, Not a Black Box</span>
          <h2 style={S.sectionTitle}>Every decision<br />explained in plain English.</h2>

          <p style={S.prose}>
            Most algorithmic trading products are black boxes. You deposit capital, you hope it works,
            and you never really know why it did what it did. When it loses, you have no way to evaluate
            whether the loss was a normal part of the strategy or a sign something is broken.
          </p>
          <p style={S.prose}>
            BerBul is built the opposite way. After each session you can see every setup the agent
            evaluated and whether it proceeded or skipped, the full reasoning behind each decision —
            confidence score, historical cases, risk flags — every 5-minute monitoring check and the
            agent's assessment at each interval, and the exact entry and exit conditions for every trade.
          </p>

          <div style={S.callout}>
            <p style={S.calloutText}>
              "Signal confidence 58% — below threshold. RL validator flagged elevated volatility regime. SKIP."
              <br /><br />
              That is the moment the system earns trust. Not the P&L number — the reasoning.
              When you can read why every decision was made, a losing trade becomes information
              rather than anxiety.
            </p>
          </div>

          <p style={S.prose}>
            This quality of transparency is what every trader who has been burned by a black box has
            always wanted. It is built into BerBul from the ground up — not a dashboard bolted onto a
            system that can't explain itself.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <div style={S.ctaSection}>
        <h2 style={S.ctaTitle}>Ready to step out of the loop?</h2>
        <p style={S.ctaSub}>
          Start in paper trading mode. No capital required. Watch the system reason through every decision before you commit a dollar.
        </p>
        <a href="/Live-New" style={S.btnPrimary}>
          Open Live Dashboard
        </a>
        <p style={{ ...S.prose, marginTop: 20, fontSize: 13, color: C.muted }}>
          Questions? demo@berbul.ai
        </p>
      </div>

    </div>
  );
}

export default OurApproach;